import { useCallback, useEffect, useRef, useState } from 'react';
import type { CSSProperties } from 'react';
import { createTelegramLinkCode, getMe } from '../api';
import type { TelegramLinkCode } from '../types';

// ============================================================================
// Привязка Telegram к веб-аккаунту (миграция 021). Показывается на сайте, если
// у пользователя нет telegram_id: кнопка «Подключить Telegram» → код на 10 минут,
// QR-код и ссылка t.me/<бот>?start=link_<код>, а также код для ручного ввода
// в Mini App. Пока код действует, раз в 3 секунды опрашиваем /auth/me и, как
// только telegram_id появился, показываем «Telegram подключён».
// ============================================================================

interface Props {
  onLinked?: () => void;
  compact?: boolean;
}

function fmtLeft(ms: number): string {
  const total = Math.max(0, Math.floor(ms / 1000));
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
}

export default function TelegramLinkCard({ onLinked, compact }: Props) {
  const [data, setData] = useState<TelegramLinkCode | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [linked, setLinked] = useState(false);
  const [linkedUsername, setLinkedUsername] = useState<string | null>(null);
  const [now, setNow] = useState(() => Date.now());
  const [copied, setCopied] = useState(false);
  const onLinkedRef = useRef(onLinked);
  onLinkedRef.current = onLinked;

  const request = useCallback(async () => {
    setLoading(true);
    setError(null);
    setCopied(false);
    try {
      const { data: d } = await createTelegramLinkCode();
      if (d.telegram_linked) {
        setLinked(true);
        setLinkedUsername(d.telegram_username ?? null);
        onLinkedRef.current?.();
        return;
      }
      setData(d);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setLoading(false);
    }
  }, []);

  const expiresAt = data?.expires_at ? new Date(data.expires_at).getTime() : 0;
  const active = !!data && !linked && expiresAt > now;

  // Таймер обратного отсчёта и опрос статуса, пока код действует.
  useEffect(() => {
    if (!active) return;
    const tick = window.setInterval(() => setNow(Date.now()), 1000);
    const poll = window.setInterval(() => {
      getMe()
        .then(({ data: me }) => {
          if (me.telegram_id) {
            setLinked(true);
            setLinkedUsername(me.telegram_username ?? null);
            onLinkedRef.current?.();
          }
        })
        .catch(() => { /* не критично */ });
    }, 3000);
    return () => { window.clearInterval(tick); window.clearInterval(poll); };
  }, [active]);

  const copy = useCallback(async () => {
    if (!data?.link) return;
    try {
      await navigator.clipboard.writeText(data.link);
      setCopied(true);
    } catch {
      setCopied(false);
    }
  }, [data]);

  const box: CSSProperties = {
    background: 'var(--tg-secondary-bg)',
    borderRadius: 12,
    padding: compact ? '12px 14px' : '14px 16px',
    marginBottom: 14,
  };

  if (linked) {
    return (
      <div style={box}>
        <div style={{ fontWeight: 700, fontSize: 15 }}>✅ Telegram подключён{linkedUsername ? ` (@${linkedUsername})` : ''}</div>
        <div className="hint" style={{ marginTop: 4, lineHeight: 1.4 }}>
          Теперь заказы, депозит и уведомления доступны и в Telegram: откройте бота и нажмите «Открыть».
        </div>
      </div>
    );
  }

  return (
    <div style={box}>
      <div style={{ fontWeight: 700, fontSize: 15 }}>Telegram не подключён</div>
      <div className="hint" style={{ marginTop: 4, lineHeight: 1.4 }}>
        Подключите Telegram, чтобы входить в приложение из бота тем же аккаунтом и получать уведомления в чат.
      </div>
      {error && <div style={{ color: '#ff3b30', fontSize: 13, marginTop: 8 }}>{error}</div>}

      {!active && (
        <button
          onClick={request}
          disabled={loading}
          style={{
            marginTop: 10,
            background: 'var(--tg-button)',
            color: 'var(--tg-button-text)',
            border: 'none',
            borderRadius: 10,
            padding: '10px 14px',
            fontSize: 14,
            fontWeight: 600,
            fontFamily: 'inherit',
            cursor: loading ? 'wait' : 'pointer',
            opacity: loading ? 0.7 : 1,
          }}
        >
          {loading ? 'Готовим код…' : data ? 'Получить новый код' : 'Подключить Telegram'}
        </button>
      )}

      {active && data && (
        <div style={{ marginTop: 12 }}>
          <div style={{ display: 'flex', gap: 14, alignItems: 'flex-start', flexWrap: 'wrap' }}>
            {data.qr_png_base64 && (
              <img
                src={`data:image/png;base64,${data.qr_png_base64}`}
                alt="QR-код для привязки Telegram"
                width={150}
                height={150}
                style={{ borderRadius: 8, background: '#fff', flex: '0 0 auto' }}
              />
            )}
            <div style={{ flex: '1 1 200px', fontSize: 14, lineHeight: 1.45 }}>
              <div>1. Отсканируйте QR телефоном или откройте ссылку.</div>
              <div>2. В боте нажмите <b>Start</b>, Telegram привяжется автоматически.</div>
              <div style={{ marginTop: 6 }}>
                Или в Mini App на экране регистрации выберите «У меня есть аккаунт на сайте» и введите код
                {' '}<b style={{ fontSize: 16, letterSpacing: 1 }}>{data.code}</b>.
              </div>
              <div className="hint" style={{ marginTop: 6 }}>Код действует ещё {fmtLeft(expiresAt - now)}.</div>
            </div>
          </div>
          <div style={{ display: 'flex', gap: 8, marginTop: 10, flexWrap: 'wrap' }}>
            <a
              href={data.link}
              target="_blank"
              rel="noreferrer"
              style={{
                background: 'var(--tg-button)',
                color: 'var(--tg-button-text)',
                borderRadius: 10,
                padding: '9px 14px',
                fontSize: 14,
                fontWeight: 600,
                textDecoration: 'none',
              }}
            >
              Открыть в Telegram
            </a>
            <button
              onClick={copy}
              style={{
                background: 'var(--tg-bg)',
                color: 'var(--tg-button)',
                border: '1px solid var(--tg-button)',
                borderRadius: 10,
                padding: '9px 14px',
                fontSize: 14,
                fontWeight: 600,
                fontFamily: 'inherit',
                cursor: 'pointer',
              }}
            >
              {copied ? 'Ссылка скопирована ✓' : 'Скопировать ссылку'}
            </button>
          </div>
        </div>
      )}
      {!active && data && !loading && (
        <div className="hint" style={{ marginTop: 8 }}>Срок действия кода истёк. Получите новый.</div>
      )}
    </div>
  );
}
